/**
 * 実時間の時計（索引 Issue #498 の段階 5）。`ClockPort` の実装。
 *
 * **コアもポートも時計を読まない**ので、実時間が入るのはここだけ。requestAnimationFrame で進め、
 * 止めたら進まない。書き出しのときはこれを使わず `createManualClock`（外からフレームを進める）に差し替える。
 */
import { ClockPort } from '../duelEditor/timeline/ports/clock';

export function createRafClock({ ms = 0, durationMs = 0, raf, nowFn } = {}) {
  const request = raf ?? ((fn) => window.requestAnimationFrame(fn));
  const cancel = raf ? () => {} : (id) => window.cancelAnimationFrame(id);
  const now = nowFn ?? (() => performance.now());
  const listeners = new Set();
  let at = ms;
  let total = durationMs;
  let playing = false;
  let speed = 1;
  let last = 0;
  let handle = null;

  const emit = () => listeners.forEach((fn) => fn(at));
  const set = (v) => { at = Math.max(0, total > 0 ? Math.min(v, total) : v); emit(); };

  const tick = () => {
    if (!playing) return;
    const t = now();
    const delta = (t - last) * speed;
    last = t;
    at = Math.max(0, at + delta);
    if (total > 0 && at >= total) { at = total; playing = false; emit(); return; }
    emit();
    handle = request(tick);
  };

  return {
    ...ClockPort.create({
      ms: () => at,
      seek: set,
      play: (rate = 1) => {
        if (playing) { speed = rate; return; }
        playing = true; speed = rate; last = now(); handle = request(tick);
        emit();
      },
      pause: () => {
        const was = playing;
        playing = false; if (handle != null) cancel(handle); handle = null;
        if (was) emit();
      },
      paused: () => !playing,
      rate: () => speed,
      duration: () => total,
      subscribe: (fn) => { listeners.add(fn); return () => listeners.delete(fn); },
    }),
    setDuration(v) { total = v; },
    dispose() { playing = false; if (handle != null) cancel(handle); listeners.clear(); },
  };
}
